/**
 * CompanyAutocomplete — Autocomplétion entreprise via API publique data.gouv.fr (Sirene v3).
 *
 * Tape soit un SIREN (9 chiffres), SIRET (14) ou une dénomination → résultats live.
 * Au clic sur un résultat, appelle `onSelect(entreprise)` avec toutes les infos extraites:
 *   { siren, siret, denomination, legal_form, address, postal_code, city, vat_number, naf_code, naf_label, headcount_range }
 */
import { useEffect, useRef, useState } from "react";
import { Search, Building2, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

export default function CompanyAutocomplete({ onSelect, placeholder = "Tapez un nom ou SIREN…", initialValue = "" }) {
  const [q, setQ] = useState(initialValue);
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(null);
  const wrapperRef = useRef(null);

  // Debounce 300ms
  useEffect(() => {
    if (!q || q.length < 2 || picked) {
      setResults([]); return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/public/insee/search", { params: { q, limit: 10 } });
        setResults(r.data.results || []);
        setOpen(true);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, picked]);

  // Click outside to close
  useEffect(() => {
    const onClick = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pick = (r) => {
    setPicked(r);
    setQ(r.denomination);
    setOpen(false);
    setResults([]);
    onSelect?.(r);
  };

  const reset = () => {
    setPicked(null);
    setQ("");
    setResults([]);
    onSelect?.(null);
  };

  return (
    <div className="relative" ref={wrapperRef} data-testid="insee-autocomplete">
      <div className="relative">
        {picked ? <CheckCircle2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-500" /> : <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />}
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); if (picked) setPicked(null); }}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          placeholder={placeholder}
          className={`w-full rounded-xl border px-9 py-2 text-sm focus:outline-none ${picked ? "border-emerald-300 bg-emerald-50/30" : "border-neutral-200 bg-white focus:border-wine-500"}`}
          data-testid="insee-input"
        />
        {loading && <Loader2 className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-neutral-400" />}
        {picked && !loading && (
          <button type="button" onClick={reset} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-bold text-neutral-600 hover:bg-neutral-200" data-testid="insee-reset">Modifier</button>
        )}
      </div>

      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 max-h-72 w-full overflow-y-auto rounded-xl border border-neutral-200 bg-white shadow-lg" data-testid="insee-results">
          {results.map((r) => (
            <button
              key={r.siret || r.siren}
              type="button"
              onClick={() => pick(r)}
              className="flex w-full flex-col items-start gap-0.5 border-b border-neutral-100 px-3 py-2 text-left text-xs hover:bg-wine-50"
              data-testid={`insee-result-${r.siren}`}
            >
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center gap-1.5 font-bold text-neutral-900">
                  <Building2 className="h-3 w-3 text-wine-500" />
                  {r.denomination}
                </div>
                {!r.is_active && <span className="rounded-full bg-red-50 px-1.5 py-0.5 text-[9px] font-bold text-red-700">CESSÉ</span>}
              </div>
              <div className="text-[10px] text-neutral-500">
                SIREN {r.siren} · {r.city} {r.postal_code} {r.naf_label ? `· ${r.naf_label}` : ""}
              </div>
            </button>
          ))}
        </div>
      )}

      {picked && (
        <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50/60 p-2 text-[11px] text-emerald-900" data-testid="insee-picked-card">
          <div className="font-bold">{picked.denomination}</div>
          <div>SIREN <strong>{picked.siren}</strong> · TVA <strong>{picked.vat_number || "n/c"}</strong></div>
          <div>{picked.address}, {picked.postal_code} {picked.city}</div>
          {picked.naf_label && <div className="text-emerald-700">NAF {picked.naf_code} · {picked.naf_label}</div>}
        </div>
      )}
    </div>
  );
}
